import * as Sentry from '@sentry/react'
import { store } from 'state/_store'
import Order from 'state/models/Order'
import { appendOrder } from 'state/Orders'
import { getActiveRestaurant } from 'state/Restaurants'
import { getSettings } from 'state/Settings'
import history from '../router/history'
import { notifyOrder } from './notification'
import platform from './platform'
import StarPrinters from './StarPrinters'

const EHUNGRY_SEC_COLOR = '#B32323'
const GENERIC_SEC_COLOR = '#301D17'

/**
 * Creates the main PushNotification object, where events can be registered, and
 * also includes some helper methods - to make PushNotification a less verbose.
 * Returns a fake object with some stub methods in case the plugin is not
 * initialized - AKA web usage.
 * @param sender {string} The Push sender ID - only used in the first call
 * @param resellerStyle {ResellerStyle|null} Which branding should we use for the notification icon?
 * @param reload {boolean} If true, the PushNotification object is reinitialized
 * @returns {Object}
 */
export default function pushWrapper(sender = '', resellerStyle = null, reload = false) {
	if (typeof pushWrapper.instance == 'undefined' || reload) {
		if (!sender) {
			console.error('Trying to instantiate PushNotification without the sender ID!')
		}

		/** @var PushNotification {object} If we're in Cordova, this is an object (from cordova-plugin-push) with push operations */
		if (typeof PushNotification != 'undefined') {
			pushWrapper.instance = PushNotification.init({ //eslint-disable-line no-undef
				//iOS notification settings are permanent from the first run, so although we
				// can default-set them from the current user settings, further changes can
				// only happen at Settings > Notif > App name
				//TODO: thus, we should probably hide those options from our own Settings screen, and tell the user they can change notification options at the Settings app
				ios: {
					alert: true,
					badge: true,
					sound: true,
					clearBadge: true
				},
				//if anything here changes, we need to re-register the push handler! i.e. turn off and on again
				android: {
					senderID: sender,
					iconColor: resellerStyle? (resellerStyle.secondary || GENERIC_SEC_COLOR) : EHUNGRY_SEC_COLOR, //used for the icon, and also app name on Android 7
					icon: resellerStyle? 'notification_generic' : 'notification' //source file at «app_repo»/«app»/platforms/android/src/main/res
				}
			})
		} else {
			console.warn('There\'s no Web Push support yet. I hope you know what you\'re doing...')
			//TODO: fake object as there's no support for browsers yey (although the plugin seems to support web push? we would still need a web push server, not sure if pubnub can do that)
			pushWrapper.instance = {
				on: e => console.debug(`push.on(${e})`, arguments),
				unregister: () => console.debug('push.unregister()'),
				finish: (a, b, id) => console.debug(`push.finish(a, b, ${id})`),
				/** @deprecated doesn't work either in Android or iOS, at least not the way we expected */
				getApplicationIconBadgeNumber: (a, b) => console.info(`push.getApplicationIconBadgeNumber(a, b)`) || a(1),
				/** @deprecated doesn't work either in Android or iOS, at least not the way we expected */
				setApplicationIconBadgeNumber: (a, b, count) => console.info(`push.setApplicationIconBadgeNumber(a, b, ${count})`),
				clearAllNotifications: (success, fail) => console.info(`push.clearAllNotifications(s, f)`),
				clearNotification: (success, fail, id) => console.info(`push.clearNotification(s, f, ${id})`),
			}
		}
	}

	return pushWrapper.instance
}

pushWrapper.unregister = () => {
	if (typeof pushWrapper.instance != 'undefined') {
		pushWrapper.instance.unregister(() => {}, e => console.error('pushWrapper.unregister error', e), [])
		pushWrapper.instance = undefined
	}
}
pushWrapper.finished = notId => {
	pushWrapper().finish(() => {}, e => console.error('pushWrapper.finish error', e), notId)
}
pushWrapper.clearNotif = notId => {
	pushWrapper().clearNotification(() => {}, e => console.error('pushWrapper.clearNotification error', e), notId)
}
/**
 * Doesn't work on current Androids - not even Nova Launcher. Nowadays they usually only show a simple dot to describe the app as having pending notifications (which is independent from this, and is exactly what the Lenovo M9's default launcher does, if configured to do so), or just show nothing at all.
 *
 * Additionally, on iOS the `getApplicationIconBadgeNumber` grabs the number sent on the push - thus, effectively, the backend would be responsible for knowing how many pending orders there are. That's not possible right now, so we're killing those two functions.
 * @deprecated
 * @see https://github.com/havesource/cordova-plugin-push/blob/master/docs/PAYLOAD.md#badges The Android implementation depended on the defunct (6yo+) ShortcutBadger
 */
pushWrapper.setBadge = () => console.error('badge operations do not work')
/** @deprecated - see details on {@link setBadge()} */
pushWrapper.getBadge = () => console.error('badge operations do not work')

/**
 * Event handlers
 */
pushWrapper.on = {
	/** @see https://github.com/havesource/cordova-plugin-push/blob/master/docs/API.md#pushonerror-callback */
	error: e => {
		console.error(`[PUSH] Internal plugin error: ${e}`, e)
		Sentry.captureMessage(`Push plugin internal error: ${e}`, {
			level: 'fatal',
			extra: { error: e }
		})
	},

	/**
	 * @param registerToken A reference to `state/Push.js`'s {@link registerToken()}
	 * @param setupDonkey   A reference to `state/Push.js`'s {@link setupDonkeyRefreshes()}
	 * @returns {(function({ registrationId: string, registrationType: 'FCM'|'APNS' }): Promise<void>)}
	 */
	registration: (registerToken, setupDonkey) => async data => {
		//no need to re-register if the token is the same... or so we hope
		const currentToken = store.getState().push.token
		if (currentToken == data.registrationId) {
			console.debug('Skipping push re-register since we already have the same token')
			return
		}

		//we need to re-run registration every time the app runs, as sometimes the device token can be changed: https://is.gd/L9m4Z8
		console.debug('registering push!', data)
		await store.dispatch(registerToken(data.registrationId)) //await so we arrive at the donkey with the final state
		store.dispatch(setupDonkey) //here it's going to disable the intervals
	},

	/**
	 * Android App Behavior manually verified (in Android 7 and 10):
	 * A-K. app is dead/killed: notification on the tray, nothing is received (React code haven't started yet, right?)
	 * A-B. app in background:  notification on the tray { foreground: false, coldstart: true,  dismissed: «undefined» }
	 * A-C. notification clicked: app opens (A-K or A-B) { foreground: false, coldstart: false, dismissed: false }
	 *    - called this handler first when it arrived, and second when it was clicked
	 *    - the same payload occurs when the app is opened from the app list, and then the notif is clicked
	 *    - [not sure?] there's no way to distinguish between stacked notifs, thus it triggers for the most recent
	 *      - thus, it might not be advisable to stack different orders, so each notif is individually actionable
	 * A-D. notification dismissed: nothing happens, besides the initial call
	 * A-F. app is already opened, in foreground:        { foreground: true, coldstart: false, dismissed: «undefined» }
	 *
	 * iOS App Behavior (iOS 14)
	 * I-B. App in background: notification on tray { foreground: false, colstart: false, dismissed: «undefined» }
	 * I-C. notification clicked: app opens         { foreground: false, colstart: true,  dismissed: «undefined» }
	 *    - called this handler first when it arrived, and second when it was clicked
	 *    - the same payload occurs when the app got killed, notification arrived, DevTools attached, and notif clicked
	 * I-D. notification dismissed: nothing
	 * I-F. app is already opened, in foreground:    { foreground: true, coldstart: false }
	 *
	 * What we need to do:
	 * 1. append the received order to the current list
	 * 2. prefetch the order complete details
	 * 3. remove the notification from the tray if it's not in the configured time frame
	 * 4. create an alert with the order link, if this happened in foreground
	 * 5. send the user to the order page if they clicked the notification
	 * 9. call finish() (mandatory for iOS)
	 *
	 * @param rawOrder {object} Basic order details - same from GET /orders
	 * @param extras {{ foreground: boolean, coldstart: boolean, dismissed: ?boolean, notId: string, style: string, 'content-available': string }}
	 * @param data {{ message: string, title: string, count: string, sound: string, image: string }}
	 */
	notification: ({ additionalData: { order: rawOrder, ...extras }, ...data }) => {
		console.debug(`[Push] ${extras.notId} | cold:${extras.coldstart} fg:${extras.foreground} dismissed:${extras.dismissed}`, { rawOrder, extras, data }) //those props need to stay in the string, so we can read it directly from Android's logcat

		//region Variables
		const state            = store.getState()
		const order            = new Order(rawOrder)
		const activeRestaurant = getActiveRestaurant(state)
		if (activeRestaurant && order.restaurant_id != activeRestaurant.id) {
			console.debug('Skipping order push altogether: does not belong to the current restaurant', { activeRestaurant, order })
			pushWrapper.clearNotif(extras.notId)
			return
		}
		//endregion

		store.dispatch(appendOrder(rawOrder)) //dedupe happens inside the action, so it's safe to be repeatedly called
		//TODO: prefetch order complete details - depends on Axios caching, see ch9281.
		// Remember: we can't depend on this, since it won't happen when the app was dead because React won't be in bg

		//region erase the notification if !order.shouldNotify()
		const { newOrderWhen, futureOrderAlert } = getSettings(state)
		if (!order.shouldNotify(newOrderWhen, futureOrderAlert)) {
			pushWrapper.clearNotif(extras.notId)
			return
		}
		//endregion

		//region Normalization of how the function got called
		/**
		 * Defines how we got this notification, if it was just received in the tray or if it was opened from the tray.
		 * This is a normalization try - it looks like iOS and Android behave in opposite ways (see this function's doc).
		 * @type {'FOREGROUND'|'RECEIVED'|'OPENED'|'DISMISSED'|'WHAT'} */
		let STATUS
		if (extras.foreground) {
			STATUS = 'FOREGROUND'
		} else if (platform.isIOS()) {
			STATUS = extras.coldstart? 'OPENED' : 'RECEIVED' // I-C : I-B
		} else if (platform.isAndroid()) {
			STATUS = extras.coldstart? 'RECEIVED' : (extras.dismissed === false? 'OPENED' : 'WHAT') // A-B : (A-C : ???)
		} else { //sanity check lol
			console.error('[Push] Unrecognized platform??', platform())
		}
		console.debug(`[Push] ${STATUS}`, { extras })
		//endregion

		switch (STATUS) {
			case 'FOREGROUND': //app already open: we only need a local alert, using normal options (includes printing)
				notifyOrder(order)
				break

			case 'RECEIVED': //app is in bg, notification arrives in tray: we should play the custom sound and print it
				StarPrinters.print(order, true)
				//FIXME: we can't play sounds from here (instead of coming from the push behavior itself) without breaking the sound for the legacy app, so playing sound during push via JS is on hold until we get rid of the old app... And this probably only works in Android anyway?
				break

			case 'OPENED': //notification is clicked: we need to take the user to the order details
				// pushWrapper.setBadge(0) //FIXME is this even needed?
				history.push(`/order/${order.id}`)
				break

			case 'DISMISSED':
			case 'WHAT':
			default:
				Sentry.captureMessage(`Unrecognized push status "${STATUS}"`, {
					level: 'error',
					extra: {
						pushPayload: {
							__NOTE__: 'This is how the Cordova push plugin splits the received payload, not exactly the raw object sent by the push service!',
							order: rawOrder,
							extras,
							data
						}
					}
				})
				console.error('[PUSH] Notification status is unrecognized!', { extras })
		}

		//and then, mark the notification as handled
		pushWrapper.finished(extras.notId)
	},
}

